import React, { useState, useEffect, useCallback } from "react";
import { NavLink as RouterLink, Link, useLocation, useHistory } from "react-router-dom";
import makeStyles from "@material-ui/styles/makeStyles";
import useTheme from "@material-ui/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Zoom from "@material-ui/core/Zoom";
import Alert from "@material-ui/lab/Alert";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import BookmarkBorderOutlinedIcon from "@material-ui/icons/BookmarkBorderOutlined";
import Drawer from "@material-ui/core/Drawer";
import {
  ProSidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  Menu,
  MenuItem,
  SubMenu,
} from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";

import { useGlobalState } from "../../../../state";
import { pages as farmPages } from "../Sidebar/Sidebar";
import api from "../../../../common/api";
import { version } from "../../../../common/enums";

export const SIDEBAR_WIDTH_EXPANDED = 260;
export const SIDEBAR_WIDTH_COLLAPSED = 80;
const MOBILE_BAR_HEIGHT = 64;
const MOBILE_BAR_MAX_WIDTH = 420;
const MOBILE_TAB_ACTIVE_BG = "#f39c12";
const BG = "#2c3e50";
const ACTIVE = "#f3b56b";

const MOBILE_SHORT_LABELS = {
  "Farm Dashboard": "Dashboard",
  "Daily Entries": "Daily Entry",
  "Daily Feed Production": "Feed Entry",
  "Batch Shed Availability": "Availability",
  "Stock Storage": "Stock",
  "Medicine Storage": "Medicines",
  "Daily Egg Rates": "Egg Rates",
  "Daily Feed Rates": "Feed Rates",
  "Add line sales": "Line Sales",
};

const getMobileTabTitle = (title) => MOBILE_SHORT_LABELS[title] || title;

const getInitials = (title) => {
  if (!title) return "??";
  const words = title.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return title.substring(0, 2).toUpperCase();
};

const useStyles = makeStyles((theme) => ({
  sidebarRoot: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    zIndex: theme.zIndex.drawer,
    transition: "width 0.3s",

    "& .pro-sidebar": {
      width: "100%",
      minWidth: "100%",
      height: "100%",
      color: "#fff",
    },
    "& .pro-sidebar-inner": {
      background: BG + " !important",
    },

    // Menu items
    "& .pro-sidebar .pro-menu .pro-menu-item": { fontSize: 14 },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item": {
      padding: "8px 20px",
      transition: "color 0.2s",
    },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item:hover": {
      color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
    },
    "& .pro-sidebar .pro-menu .pro-menu-item.active > .pro-inner-item": {
      backgroundColor: "rgba(243,181,107,0.15)",
      borderRadius: 8,
    },

    // Links
    "& .pro-sidebar .pro-menu a": { color: "inherit", textDecoration: "none" },
    "& .pro-sidebar .pro-menu a:hover": {
      color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
    },
    "& .pro-sidebar .pro-menu a.active": {
      color: theme.palette.navActive || ACTIVE,
    },

    // Icons
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item > .pro-icon-wrapper": {
      minWidth: 24, width: 24, height: 24, lineHeight: "24px", fontSize: 20,
      marginRight: 10,
      color: theme.palette.icon || "#c5c7c9",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "color 0.2s",
    },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item > .pro-icon-wrapper .pro-icon": {
      lineHeight: 1, display: "flex", alignItems: "center",
      animation: "none !important",
    },
    "& .pro-sidebar .pro-menu .pro-menu-item.active > .pro-inner-item > .pro-icon-wrapper": {
      color: theme.palette.navActive || ACTIVE,
    },
    "& .pro-sidebar .pro-menu .pro-menu-item > .pro-inner-item:hover > .pro-icon-wrapper": {
      color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
    },

    // Suffix (bookmark icons)
    "& .pro-sidebar .pro-menu .pro-menu-item .suffix-wrapper": {
      opacity: 0.7,
    },

    // SubMenu nested list
    "& .pro-sidebar .pro-menu > ul > .pro-sub-menu > .pro-inner-list-item": {
      backgroundColor: "transparent",
      paddingLeft: 16,
    },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-inner-item": {
      padding: "10px 16px 10px 20px",
    },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-inner-item:before": {
      display: "none",
    },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-menu-item": {
      borderLeft: "2px solid rgba(255,255,255,0.08)",
      marginLeft: 12,
      marginBottom: 2,
    },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-menu-item:hover": {
      borderLeftColor: "rgba(243,181,107,0.4)",
    },
    "& .pro-sidebar .pro-menu .pro-sub-menu .pro-inner-list-item .pro-menu-item.active": {
      borderLeftColor: theme.palette.navActive || ACTIVE,
    },

    // SubMenu arrow
    "& .pro-sidebar .pro-menu .pro-sub-menu > .pro-inner-item > .pro-arrow-wrapper .pro-arrow": {
      borderColor: theme.palette.navNestedMenuArrow || "rgba(255,255,255,0.7)",
    },

    // Hide icon wrapper in nested items
    "& .pro-sidebar .pro-menu .pro-menu-item.pro-sub-menu .pro-inner-list-item .pro-inner-item > .pro-icon-wrapper": {
      display: "none",
    },

    // ── Collapsed mode popper styling ──
    "& .pro-sidebar.collapsed .pro-menu > ul > .pro-menu-item.pro-sub-menu > .pro-inner-list-item > .popper-inner": {
      backgroundColor: BG,
    },
    "& .pro-sidebar.collapsed .pro-menu > ul > .pro-menu-item.pro-sub-menu:hover .pro-icon-wrapper .pro-icon": {
      animation: "none !important",
    },
    "& .pro-sidebar.collapsed .pro-menu > ul > .pro-menu-item.pro-sub-menu .pro-inner-list-item .pro-inner-item:hover": {
      color: theme.palette.navHover ? theme.palette.navHover.text : ACTIVE,
    },
    // Popper arrow
    "& .popper-element[data-popper-placement^='right'] > .popper-arrow": {
      borderRightColor: BG + " !important",
    },
  },
}));

const FarmSidebarPro = ({ match, onExpandChange }) => {
  const [{ user, sidebarCollapsed }, dispatch] = useGlobalState();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"), { defaultMatches: true });
  const location = useLocation();
  const history = useHistory();
  const classes = useStyles();

  var collapsed = sidebarCollapsed || false;
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileSubmenuPage, setMobileSubmenuPage] = useState(null);
  const [allPages, setAllPages] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [openMenus, setOpenMenus] = useState({});

  var currentPages = farmPages({ user, match });

  // ── Bookmarks ──
  const fetchBookmarks = useCallback(() => {
    const base = currentPages.filter((p) => p.id === undefined);
    var total = [...base];
    api
      .get("get-all-bookmarks")
      .then((res) => {
        setBookmarks(res || []);
        var idx = user.versionID && user.versionID !== version.proPlus ? 1 : 2;
        total.splice(idx, 0, ...res);
      })
      .catch((err) => {
        if (err.message) {
          if (err.message === "Bad Request" || err.message === "No data found")
            setErrorMessage("Bookmarks not found");
        } else {
          setErrorMessage("Unknown Error");
        }
      })
      .finally(() => setAllPages(total));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, match]);

  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  var removeBookmark = (page) => {
    var bookmark = bookmarks.find(
      (b) => b.title === page.title && b.href === page.href
    );
    api
      .post("remove-bookmark", bookmark || page)
      .then(() => {})
      .catch((err) => {
        if (err.message) {
          if (err.message === "Bad Request" || err.message === "No data found")
            setErrorMessage("Remove bookmark failed");
          else setErrorMessage(err.message);
        } else {
          setErrorMessage("Unknown Error");
        }
      })
      .finally(() => fetchBookmarks());
  };

  var createBookmark = (page) => {
    api
      .post("create-new-bookmark", { title: page.title, href: page.href })
      .then(() => {})
      .catch((err) => {
        if (err.message) {
          if (err.message === "Bad Request" || err.message === "No data found")
            setErrorMessage("Bookmark not added");
          else setErrorMessage(err.message);
        } else {
          setErrorMessage("Unknown Error");
        }
      })
      .finally(() => fetchBookmarks());
  };

  var isPageBookmarked = (page) => {
    return bookmarks.some(
      (b) => b.title === page.title && b.href === page.href
    );
  };

  var handleToggle = () => {
    var next = !collapsed;
    dispatch({ type: "setSidebarCollapsed", payload: next });
    if (onExpandChange) onExpandChange(!next);
  };

  var handleLogout = () => dispatch({ type: "logout" });

  var displayPages = allPages.length > 0 ? allPages : currentPages;

  var isPathActive = (href) =>
    Boolean(
      href &&
        (location.pathname === href ||
          location.pathname.startsWith(href + "/"))
    );

  var isActive = (page) => {
    if (page.href) return isPathActive(page.href);
    if (page.subMenus) {
      return page.subMenus.some((s) => {
        if (s.href && isPathActive(s.href)) return true;
        if (s.subMenus)
          return s.subMenus.some((d) => d.href && isPathActive(d.href));
        return false;
      });
    }
    return false;
  };

  var getHref = (page) => {
    if (page.href) return page.href;
    if (page.subMenus && page.subMenus.length > 0) {
      var first = page.subMenus[0];
      if (first.href) return first.href;
      if (first.subMenus && first.subMenus.length > 0)
        return first.subMenus[0].href;
    }
    return "/";
  };

  var visiblePages = displayPages.filter(
    (p) => p.icon || p.nested || p.isBookmarked === true
  );

  // Theme colors
  var activeColor = theme.palette.navActive || ACTIVE;
  var iconColor = theme.palette.icon || "#c5c7c9";

  // Profile
  var profileName = user.firstName + " " + user.lastName;
  var profileAvatar = user.profilePic || "/images/chick.webp";
  var profileRole = user.role || "Managing Director";

  // Letter icon styles
  var letterIcon = {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: "rgba(255,255,255,0.12)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    textTransform: "uppercase",
    lineHeight: 1,
  };

  var smallLetterIcon = {
    ...letterIcon,
    width: 22,
    height: 22,
    fontSize: 9,
  };

  // Bookmark suffix helper
  var bookmarkSuffix = (page) => {
    if (!page.href) return undefined;
    var bookmarked = isPageBookmarked(page);
    return (
      <Tooltip
        title={bookmarked ? "Remove bookmark" : "Add bookmark"}
        placement="top"
        arrow
        TransitionComponent={Zoom}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (bookmarked) removeBookmark(page);
            else createBookmark(page);
          }}
          style={{ padding: 2, color: iconColor }}
        >
          {bookmarked ? (
            <BookmarkIcon style={{ fontSize: 16 }} />
          ) : (
            <BookmarkBorderOutlinedIcon style={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  // ════════════════════════════════════════════
  // MOBILE — bottom nav bar
  // ════════════════════════════════════════════
  var mobileHoverColor = theme.palette.navHover ? theme.palette.navHover.text : ACTIVE;

  if (!isDesktop) {
    var nonBookmarkPages = visiblePages.filter((p) => p.isBookmarked !== true);
    var bookmarkPages = displayPages.filter((p) => p.isBookmarked === true);
    var tabs = nonBookmarkPages.slice(0, 4);
    var overflow = nonBookmarkPages.slice(4);
    var isMoreActive = overflow.some(isActive) || bookmarkPages.some(isActive);

    return (
      <>
        <style>{`
          .mobile-drawer-link { transition: color 0.2s; }
          .mobile-drawer-link:hover { color: ${mobileHoverColor} !important; }
          .mobile-nav-icon .MuiSvgIcon-root { font-size: 20px; }
        `}</style>
        <nav
          style={{
            // floating bar: inset from the edges, centred, capped width
            position: "fixed",
            bottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 28px)",
            maxWidth: MOBILE_BAR_MAX_WIDTH,
            height: MOBILE_BAR_HEIGHT,
            backgroundColor: BG,
            borderRadius: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            gap: 4,
            zIndex: theme.zIndex.appBar + 1,
            boxShadow: "0 8px 28px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.16)",
            border: "1px solid rgba(255,255,255,0.08)",
            padding: 6,
            boxSizing: "border-box",
          }}
        >
          {tabs.map((page) => {
            var active = isActive(page);
            return (
              <button
                key={page.title}
                onClick={() => {
                  if (page.nested && page.subMenus) {
                    setMobileSubmenuPage(page);
                    setMobileDrawerOpen(true);
                  } else {
                    history.push(getHref(page));
                  }
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  // selected tab: yellow pill inset in the bar, dark icon + text
                  height: "100%",
                  padding: "4px 6px",
                  border: "none",
                  borderRadius: 18,
                  background: active ? MOBILE_TAB_ACTIVE_BG : "transparent",
                  color: active ? "#1a252f" : "rgba(255,255,255,0.65)",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  minWidth: 0,
                  transition: "background-color 0.2s ease, color 0.2s ease",
                }}
              >
                <span
                  className="mobile-nav-icon"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 22,
                    marginBottom: 2,
                    lineHeight: 1,
                  }}
                >
                  {page.icon || (
                    <span style={smallLetterIcon}>
                      {getInitials(page.title)}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: active ? 600 : 500,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {getMobileTabTitle(page.title)}
                </span>
              </button>
            );
          })}

          {/* More tab — always show for back-to-main + overflow + logout */}
          <button
            onClick={() => {
              setMobileSubmenuPage(null);
              setMobileDrawerOpen(true);
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              height: "100%",
              padding: "4px 6px",
              border: "none",
              borderRadius: 18,
              background: isMoreActive ? MOBILE_TAB_ACTIVE_BG : "transparent",
              color: isMoreActive ? "#1a252f" : "rgba(255,255,255,0.65)",
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
              minWidth: 0,
              transition: "background-color 0.2s ease, color 0.2s ease",
            }}
          >
            <span
              className="mobile-nav-icon"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 22,
                marginBottom: 2,
                lineHeight: 1,
              }}
            >
              <MoreHorizIcon />
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: isMoreActive ? 600 : 500,
                lineHeight: 1.15,
              }}
            >
              More
            </span>
          </button>
        </nav>

        <Drawer
          anchor="bottom"
          open={mobileDrawerOpen}
          onClose={() => {
            setMobileDrawerOpen(false);
            setMobileSubmenuPage(null);
          }}
          PaperProps={{
            style: {
              backgroundColor: BG,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "80%",
            },
          }}
        >
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.3)",
              margin: "10px auto 4px",
            }}
          />
          <div style={{ padding: "8px 16px 16px", overflowY: "auto" }}>
            {mobileSubmenuPage ? (
              <>
                {/* Header with back arrow and page title */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 0 8px",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setMobileSubmenuPage(null)}
                    style={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    <ChevronLeftIcon style={{ fontSize: 20 }} />
                  </IconButton>
                  <span
                    style={{
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 600,
                    }}
                  >
                    {mobileSubmenuPage.title}
                  </span>
                </div>
                <div
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    margin: "0 0 8px",
                  }}
                />
                {/* Submenu items */}
                {mobileSubmenuPage.subMenus.map((sub) => {
                  if (sub.nested && sub.subMenus) {
                    return (
                      <React.Fragment key={sub.title}>
                        <div
                          style={{
                            color: "rgba(255,255,255,0.5)",
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            padding: "8px 8px 4px",
                          }}
                        >
                          {sub.title}
                        </div>
                        {sub.subMenus.map((deep) => {
                          var deepActive = isPathActive(deep.href);
                          return (
                            <RouterLink
                              className="mobile-drawer-link"
                              key={deep.title}
                              to={deep.href || "/"}
                              onClick={() => {
                                setMobileDrawerOpen(false);
                                setMobileSubmenuPage(null);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "10px 12px 10px 28px",
                                borderRadius: 10,
                                textDecoration: "none",
                                color: deepActive ? activeColor : "#fff",
                                backgroundColor: deepActive
                                  ? "rgba(243,181,107,0.1)"
                                  : "transparent",
                                fontSize: 14,
                              }}
                            >
                              {deep.title}
                            </RouterLink>
                          );
                        })}
                      </React.Fragment>
                    );
                  }
                  var subActive = isPathActive(sub.href);
                  return (
                    <RouterLink
                      className="mobile-drawer-link"
                      key={sub.title}
                      to={sub.href || "/"}
                      onClick={() => {
                        setMobileDrawerOpen(false);
                        setMobileSubmenuPage(null);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        borderRadius: 10,
                        textDecoration: "none",
                        color: subActive ? activeColor : "#fff",
                        backgroundColor: subActive
                          ? "rgba(243,181,107,0.1)"
                          : "transparent",
                        fontSize: 14,
                      }}
                    >
                      {sub.title}
                    </RouterLink>
                  );
                })}
              </>
            ) : (
              <>
                {/* Back to Main Menu */}
                <button
                  onClick={() => {
                    setMobileDrawerOpen(false);
                    setMobileSubmenuPage(null);
                    history.push("/");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "none",
                    background: "transparent",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  <ArrowBackIcon style={{ fontSize: 20 }} />
                  Back to Main Menu
                </button>
                <div
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    margin: "8px 0",
                  }}
                />

                {/* Overflow pages */}
                {overflow.map((page) => {
                  if (page.nested && page.subMenus) {
                    return (
                      <React.Fragment key={page.title}>
                        <div
                          style={{
                            color: "rgba(255,255,255,0.5)",
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            padding: "8px 8px 4px",
                          }}
                        >
                          {page.title}
                        </div>
                        {page.subMenus.map((sub) => {
                          if (sub.nested && sub.subMenus) {
                            return sub.subMenus.map((deep) => {
                              var deepActive = isPathActive(deep.href);
                              return (
                                <RouterLink
                                  className="mobile-drawer-link"
                                  key={deep.title}
                                  to={deep.href || "/"}
                                  onClick={() => {
                                    setMobileDrawerOpen(false);
                                    setMobileSubmenuPage(null);
                                  }}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "10px 12px 10px 28px",
                                    borderRadius: 10,
                                    textDecoration: "none",
                                    color: deepActive ? activeColor : "#fff",
                                    backgroundColor: deepActive
                                      ? "rgba(243,181,107,0.1)"
                                      : "transparent",
                                    fontSize: 14,
                                  }}
                                >
                                  {deep.title}
                                </RouterLink>
                              );
                            });
                          }
                          var subActive = isPathActive(sub.href);
                          return (
                            <RouterLink
                              className="mobile-drawer-link"
                              key={sub.title}
                              to={sub.href || "/"}
                              onClick={() => {
                                setMobileDrawerOpen(false);
                                setMobileSubmenuPage(null);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "10px 12px 10px 28px",
                                borderRadius: 10,
                                textDecoration: "none",
                                color: subActive ? activeColor : "#fff",
                                backgroundColor: subActive
                                  ? "rgba(243,181,107,0.1)"
                                  : "transparent",
                                fontSize: 14,
                              }}
                            >
                              {sub.title}
                            </RouterLink>
                          );
                        })}
                      </React.Fragment>
                    );
                  }

                  var active = isActive(page);
                  return (
                    <RouterLink
                      className="mobile-drawer-link"
                      key={page.title}
                      to={page.href || "/"}
                      onClick={() => {
                        setMobileDrawerOpen(false);
                        setMobileSubmenuPage(null);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        borderRadius: 10,
                        textDecoration: "none",
                        color: active ? activeColor : "#fff",
                        backgroundColor: active
                          ? "rgba(243,181,107,0.1)"
                          : "transparent",
                        fontSize: 14,
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          color: active ? activeColor : "rgba(255,255,255,0.5)",
                        }}
                      >
                        {page.icon || (
                          <span style={smallLetterIcon}>
                            {getInitials(page.title)}
                          </span>
                        )}
                      </span>
                      {page.title}
                    </RouterLink>
                  );
                })}

                {/* Bookmarks */}
                {bookmarkPages.length > 0 && (
                  <>
                    <div
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        margin: "8px 0",
                      }}
                    />
                    <div
                      style={{
                        color: "rgba(255,255,255,0.5)",
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        padding: "8px 8px 4px",
                      }}
                    >
                      Bookmarks
                    </div>
                    {bookmarkPages.map((page) => {
                      var bActive = isPathActive(page.href);
                      return (
                        <RouterLink
                          className="mobile-drawer-link"
                          key={page.id + "-" + page.title}
                          to={page.href || "/"}
                          onClick={() => {
                            setMobileDrawerOpen(false);
                            setMobileSubmenuPage(null);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 12px",
                            borderRadius: 10,
                            textDecoration: "none",
                            color: bActive ? activeColor : "#fff",
                            backgroundColor: bActive
                              ? "rgba(243,181,107,0.1)"
                              : "transparent",
                            fontSize: 14,
                          }}
                        >
                          <span style={smallLetterIcon}>
                            {getInitials(page.title)}
                          </span>
                          {page.title}
                          <BookmarkIcon
                            style={{
                              fontSize: 16,
                              color: iconColor,
                              marginLeft: "auto",
                              opacity: 0.7,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeBookmark(page);
                            }}
                          />
                        </RouterLink>
                      );
                    })}
                  </>
                )}

                <div
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    margin: "8px 0",
                  }}
                />
                <button
                  onClick={handleLogout}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "none",
                    background: "transparent",
                    color: "#ff6b6b",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  <ExitToAppIcon style={{ fontSize: 20 }} />
                  Logout
                </button>
              </>
            )}
          </div>
        </Drawer>
      </>
    );
  }

  // ════════════════════════════════════════════
  // DESKTOP — Single ProSidebar (collapsed + expanded)
  // ════════════════════════════════════════════
  return (
    <div
      className={classes.sidebarRoot}
      style={{
        width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
      }}
    >
      <ProSidebar
        collapsed={collapsed}
        width={SIDEBAR_WIDTH_EXPANDED + "px"}
        collapsedWidth={SIDEBAR_WIDTH_COLLAPSED + "px"}
      >
        <SidebarHeader>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: collapsed ? "16px 0 8px" : "16px 8px",
              position: "relative",
            }}
          >
            <Link to="/">
              <img
                alt="Logo"
                src="/images/logos/poultry-client-logo.png"
                style={{
                  width: collapsed ? 36 : 50,
                  marginBottom: collapsed ? 0 : 8,
                }}
              />
            </Link>

            {/* Profile — only when expanded */}
            {!collapsed && (
              <div
                style={{
                  textAlign: "center",
                  backgroundColor: theme.palette.profile
                    ? theme.palette.profile.box
                    : "rgba(255,255,255,0.3)",
                  padding: 12,
                  borderRadius: 12,
                  width: "100%",
                  marginTop: 8,
                }}
              >
                <Avatar
                  alt="Person"
                  src={profileAvatar}
                  style={{
                    width: 50,
                    height: 50,
                    margin: "0 auto",
                    marginBottom: 4,
                  }}
                />
                <Typography
                  variant="body1"
                  style={{
                    color: theme.palette.profile
                      ? theme.palette.profile.name
                      : "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {profileName}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: theme.palette.profile
                      ? theme.palette.profile.bio
                      : ACTIVE,
                    fontSize: 12,
                  }}
                >
                  {profileRole} ({user.versionName})
                </Typography>
              </div>
            )}

            {/* Toggle button */}
            <div
              style={
                collapsed
                  ? {
                      display: "flex",
                      justifyContent: "center",
                      padding: "4px 0",
                    }
                  : { position: "absolute", top: 8, right: 8 }
              }
            >
              <IconButton
                onClick={handleToggle}
                size="small"
                style={{
                  width: 28,
                  height: 28,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  color: iconColor,
                }}
              >
                {collapsed ? (
                  <ChevronRightIcon style={{ fontSize: 18 }} />
                ) : (
                  <ChevronLeftIcon style={{ fontSize: 18 }} />
                )}
              </IconButton>
            </div>
          </div>

          {/* Back to Main Menu */}
          {collapsed ? (
            <div style={{ textAlign: "center", marginBottom: 4 }}>
              <Tooltip title="Back to Main Menu" placement="right" arrow>
                <IconButton
                  onClick={() => history.push("/")}
                  size="small"
                  style={{ color: "rgba(255,255,255,0.7)" }}
                >
                  <ArrowBackIcon style={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            <>
              <div style={{ margin: "0 16px" }} />
              <button
                onClick={() => history.push("/")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 20px",
                  border: "none",
                  background: "transparent",
                  color: theme.palette.buttonColor
                    ? theme.palette.buttonColor.primary
                    : "#fff",
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <ArrowBackIcon style={{ color: iconColor, fontSize: 22 }} />
                Back to Main Menu
              </button>
              <div style={{ margin: "0 16px" }} />
            </>
          )}
        </SidebarHeader>

        <SidebarContent>
          {errorMessage && (
            <div style={{ margin: "0 8px 8px" }}>
              <Alert severity="error" onClose={() => setErrorMessage("")}>
                {errorMessage}
              </Alert>
            </div>
          )}

          <Menu>
            {displayPages.map((page) => {
              // ── Nested → SubMenu ──
              if (page.nested && page.subMenus) {
                return (
                  <SubMenu
                    key={page.title}
                    title={collapsed ? null : page.title}
                    open={openMenus[page.title] || false}
                    onOpenChange={(val) =>
                      setOpenMenus((prev) => ({
                        ...prev,
                        [page.title]: val,
                      }))
                    }
                    icon={
                      page.icon || (
                        <span style={letterIcon}>
                          {getInitials(page.title)}
                        </span>
                      )
                    }
                  >
                    {page.subMenus.map((sub) => {
                      // Deep nested SubMenu
                      if (sub.nested && sub.subMenus) {
                        return (
                          <SubMenu
                            key={sub.title}
                            title={sub.title}
                            open={openMenus[sub.title] || false}
                            onOpenChange={(val) =>
                              setOpenMenus((prev) => ({
                                ...prev,
                                [sub.title]: val,
                              }))
                            }
                          >
                            {sub.subMenus.map((deep) => (
                              <MenuItem
                                key={deep.title}
                                suffix={bookmarkSuffix(deep)}
                              >
                                <RouterLink
                                  to={deep.href}
                                  activeClassName="active"
                                >
                                  {deep.title}
                                </RouterLink>
                              </MenuItem>
                            ))}
                          </SubMenu>
                        );
                      }
                      // Regular sub item
                      return (
                        <MenuItem key={sub.title} suffix={bookmarkSuffix(sub)}>
                          <RouterLink to={sub.href} activeClassName="active">
                            {sub.title}
                          </RouterLink>
                        </MenuItem>
                      );
                    })}
                  </SubMenu>
                );
              }

              // ── Regular item with icon ──
              if (page.icon) {
                return (
                  <MenuItem key={page.title} icon={page.icon}>
                    <RouterLink to={page.href} activeClassName="active">
                      {collapsed ? null : page.title}
                    </RouterLink>
                  </MenuItem>
                );
              }

              // ── Bookmark item ──
              if (page.isBookmarked === true) {
                return (
                  <MenuItem
                    key={page.id + "-" + page.title}
                    icon={
                      <span style={letterIcon}>
                        {getInitials(page.title)}
                      </span>
                    }
                    suffix={
                      <Tooltip
                        title="Remove bookmark"
                        placement="top"
                        arrow
                        TransitionComponent={Zoom}
                      >
                        <BookmarkIcon
                          style={{
                            color: iconColor,
                            fontSize: 18,
                            cursor: "pointer",
                            marginLeft: 4,
                          }}
                          fontSize="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeBookmark(page);
                          }}
                        />
                      </Tooltip>
                    }
                  >
                    <RouterLink to={page.href} activeClassName="active">
                      {collapsed ? null : page.title}
                    </RouterLink>
                  </MenuItem>
                );
              }

              return null;
            })}
          </Menu>
        </SidebarContent>

        <SidebarFooter>
          <div
            style={{
              padding: "8px 16px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {collapsed ? (
              <Tooltip title="Logout" placement="right" arrow>
                <IconButton
                  onClick={handleLogout}
                  style={{
                    color: theme.palette.logoutSidebar
                      ? theme.palette.logoutSidebar.background
                      : ACTIVE,
                  }}
                >
                  <ExitToAppIcon style={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <button
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <ExitToAppIcon
                  style={{
                    color: theme.palette.logoutSidebar
                      ? theme.palette.logoutSidebar.background
                      : ACTIVE,
                    fontSize: 22,
                  }}
                />
                Logout
              </button>
            )}
          </div>
        </SidebarFooter>
      </ProSidebar>
    </div>
  );
};

export default FarmSidebarPro;
